import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Broadcast, FilmStrip, Gift, UsersThree, ArrowRight } from "@phosphor-icons/react";

const SLIDES = [
  {
    key: "create",
    icon: FilmStrip,
    title: "Dance.",
    sub: "Shoot vertical dance clips, drop a caption, post in seconds. Every style — from soca and hip-hop to K-pop, salsa and Afrobeats.",
  },
  {
    key: "share",
    icon: UsersThree,
    title: "Connect.",
    sub: "Like, comment, save, follow. Find your crew across every continent — one global dance floor.",
  },
  {
    key: "golive",
    icon: Broadcast,
    title: "Go Live.",
    sub: "One tap and you're broadcasting in HD with real-time chat, live captions, and instant replay.",
  },
  {
    key: "earn",
    icon: Gift,
    title: "Get gifted.",
    sub: "Fans send waves; you earn pearls. Cash out via PayPal in your local currency. Real money, real recognition.",
  },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);

  const finish = () => {
    try { window.localStorage.setItem("gw_onboarded", "1"); } catch { /* ignore */ }
    nav("/", { replace: true });
  };

  const Icon = SLIDES[step].icon;
  const isLast = step === SLIDES.length - 1;

  return (
    <div className="min-h-[100dvh] bg-ink flex flex-col" data-testid="onboarding-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 pt-6">
        <p className="text-papaya text-[10px] tracking-[0.35em] uppercase font-black">Giffwurldaunce</p>
        <button
          data-testid="onboarding-skip"
          onClick={finish}
          className="text-cream/60 text-[10px] tracking-[0.3em] uppercase font-bold hover:text-cream transition-colors"
        >
          Skip
        </button>
      </div>

      {/* Slide */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={SLIDES[step].key}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="flex flex-col items-center max-w-sm"
          >
            <div className="w-28 h-28 rounded-[40%] bg-gradient-to-br from-papaya/20 via-jam-green/15 to-jam-gold/20 border border-papaya/30 flex items-center justify-center papaya-glow mb-8">
              <Icon size={56} weight="duotone" className="text-papaya" />
            </div>
            <h1 className="text-cream text-5xl sm:text-6xl font-black tracking-tighter leading-none">
              {SLIDES[step].title}
            </h1>
            <p className="text-cream/65 text-sm sm:text-base mt-5 leading-relaxed">{SLIDES[step].sub}</p>
            <span className="flex items-center gap-0.5 mt-6" aria-hidden>
              <span className="w-1.5 h-1.5 rounded-full bg-jam-black ring-1 ring-white/20" />
              <span className="w-1.5 h-1.5 rounded-full bg-jam-green" />
              <span className="w-1.5 h-1.5 rounded-full bg-jam-gold" />
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-2 pb-6">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            data-testid={`onboarding-dot-${i}`}
            onClick={() => setStep(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-8 bg-papaya" : "w-1.5 bg-white/20"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* CTA */}
      <div className="px-6 pb-10">
        <button
          data-testid="onboarding-next"
          onClick={() => (isLast ? finish() : setStep(step + 1))}
          className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors papaya-glow flex items-center justify-center gap-2"
        >
          {isLast ? "Enter Giffwurldaunce" : "Next"}
          <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
